import { adminApi } from '../admin-api.ts';
import { supabase } from '../supabase.ts';

/**
 * Spreadsheet import.
 *
 * The file never leaves the browser: it is parsed here, mapped here, checked
 * here, and committed as ordinary scoped inserts under RLS. `import_jobs`
 * and `import_records` are the audit trail -- what was brought in, from
 * where, and what happened to each row -- which is the part a regulator
 * asks about and the part a merchant needs when a price arrives wrong.
 */

export type ImportEntity = 'product' | 'customer' | 'package';

export interface ImportField {
  key: string;
  label: string;
  required?: boolean;
  hint?: string;
}

/** What each importable entity needs, in the order the mapper shows them. */
export const IMPORT_FIELDS: Record<ImportEntity, ImportField[]> = {
  product: [
    { key: 'name', label: 'Product name', required: true },
    { key: 'sku', label: 'SKU', hint: 'Matches an existing variant to update it.' },
    { key: 'price', label: 'Price', required: true, hint: 'Dollars, e.g. 34.99' },
    { key: 'category', label: 'Category', hint: 'Matched by name; created if missing.' },
    { key: 'brand', label: 'Brand', hint: 'Matched by name; created if missing.' },
    { key: 'strain_type', label: 'Strain type' },
    { key: 'thc_percent', label: 'THC %' },
    { key: 'cbd_percent', label: 'CBD %' },
    { key: 'unit', label: 'Unit', hint: 'each, gram, eighth, quarter, half, ounce…' },
    { key: 'weight_grams', label: 'Weight (g)' },
    { key: 'description', label: 'Description' },
    { key: 'image_url', label: 'Image URL' },
  ],
  customer: [
    { key: 'email', label: 'Email', required: true },
    { key: 'first_name', label: 'First name' },
    { key: 'last_name', label: 'Last name' },
    { key: 'phone', label: 'Phone' },
    { key: 'birthday', label: 'Date of birth', required: true, hint: 'YYYY-MM-DD' },
    { key: 'customer_type', label: 'Customer type' },
    { key: 'marketing_opt_in', label: 'Marketing opt-in', hint: 'true / false' },
  ],
  package: [
    { key: 'label', label: 'Package label', required: true, hint: 'The Metrc tag.' },
    { key: 'sku', label: 'Variant SKU', required: true },
    { key: 'quantity', label: 'Quantity', required: true },
    { key: 'received_at', label: 'Received', hint: 'YYYY-MM-DD' },
  ],
};

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/**
 * A CSV parser that understands quotes.
 *
 * Not a library: the whole grammar is four cases, and a dependency that
 * ships a stream API and encoding detection to read a 500-row price list is
 * not worth the bytes on a page that already carries a map renderer.
 */
export function parseCsv(text: string): ParsedCsv {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let quoted = false;

  const pushValue = () => {
    row.push(value);
    value = '';
  };

  const pushRow = () => {
    pushValue();

    // A trailing newline is not a row of one empty cell.
    if (row.length > 1 || row[0] !== '') rows.push(row);

    row = [];
  };

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (quoted) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          value += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        value += char;
      }

      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      pushValue();
    } else if (char === '\n') {
      pushRow();
    } else if (char !== '\r') {
      value += char;
    }
  }

  if (value !== '' || row.length) pushRow();

  const [headers = [], ...body] = rows;

  return { headers: headers.map((header) => header.trim()), rows: body };
}

/** Guess a mapping from the file's own headers, so most files need no work. */
export function guessMapping(entity: ImportEntity, headers: string[]): Record<string, string> {
  const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
  const mapping: Record<string, string> = {};

  for (const field of IMPORT_FIELDS[entity]) {
    const target = normalise(field.key);
    const label = normalise(field.label);

    const match = headers.find((header) => {
      const candidate = normalise(header);

      return candidate === target || candidate === label;
    });

    if (match) mapping[field.key] = match;
  }

  return mapping;
}

export interface RowIssue {
  row: number;
  message: string;
}

export interface DryRunResult {
  create: number;
  update: number;
  issues: RowIssue[];
}

const STRAIN_TYPES = new Set([
  'sativa',
  'indica',
  'hybrid',
  'sativa_hybrid',
  'indica_hybrid',
  'cbd',
  'ratio_1_1',
  'ratio_1_2',
  'ratio_1_4',
  'ratio_2_1',
  'ratio_4_1',
  'na',
]);

const UNITS = new Set(['each', 'gram', 'eighth', 'quarter', 'half', 'ounce', 'milligram', 'pound']);

function cell(row: string[], headers: string[], mapping: Record<string, string>, key: string) {
  const header = mapping[key];

  if (!header) return '';

  const index = headers.indexOf(header);

  return index === -1 ? '' : (row[index] ?? '').trim();
}

/**
 * Check every row without writing anything.
 *
 * The point of the dry run is that a 900-row price list with one bad date
 * tells you which row before it half-imports.
 */
export async function dryRun(
  accountId: string,
  entity: ImportEntity,
  parsed: ParsedCsv,
  mapping: Record<string, string>,
): Promise<DryRunResult> {
  const issues: RowIssue[] = [];
  let create = 0;
  let update = 0;

  const existingSkus = new Set<string>();
  const existingEmails = new Set<string>();

  if (entity === 'product' || entity === 'package') {
    const { data } = await supabase
      .from('product_variants')
      .select('sku')
      .eq('account_id', accountId)
      .not('sku', 'is', null);

    for (const row of data ?? []) if (row.sku) existingSkus.add(row.sku.toLowerCase());
  }

  if (entity === 'customer') {
    const { data } = await supabase
      .from('customers')
      .select('email')
      .eq('account_id', accountId)
      .not('email', 'is', null);

    for (const row of data ?? []) if (row.email) existingEmails.add(row.email.toLowerCase());
  }

  parsed.rows.forEach((row, index) => {
    const line = index + 2; // 1-indexed, plus the header row.
    const get = (key: string) => cell(row, parsed.headers, mapping, key);

    for (const field of IMPORT_FIELDS[entity]) {
      if (field.required && !get(field.key)) {
        issues.push({ row: line, message: `${field.label} is required` });
      }
    }

    if (entity === 'product') {
      const price = get('price');

      if (price && !/^\$?\d+(\.\d{1,2})?$/.test(price)) {
        issues.push({ row: line, message: `Price "${price}" is not an amount` });
      }

      const strain = get('strain_type');

      if (strain && !STRAIN_TYPES.has(strain.toLowerCase())) {
        issues.push({ row: line, message: `Strain type "${strain}" is not one we know` });
      }

      const unit = get('unit');

      if (unit && !UNITS.has(unit.toLowerCase())) {
        issues.push({ row: line, message: `Unit "${unit}" is not one we know` });
      }

      const sku = get('sku').toLowerCase();

      if (sku && existingSkus.has(sku)) update += 1;
      else create += 1;
    }

    if (entity === 'customer') {
      const email = get('email').toLowerCase();
      const birthday = get('birthday');

      if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        issues.push({ row: line, message: `"${email}" is not an email address` });
      }

      if (birthday && !/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
        issues.push({ row: line, message: `Date of birth "${birthday}" is not YYYY-MM-DD` });
      }

      if (email && existingEmails.has(email)) update += 1;
      else create += 1;
    }

    if (entity === 'package') {
      const sku = get('sku').toLowerCase();
      const quantity = get('quantity');

      if (sku && !existingSkus.has(sku)) {
        issues.push({ row: line, message: `No variant with SKU "${get('sku')}"` });
      }

      if (quantity && !/^\d+(\.\d+)?$/.test(quantity)) {
        issues.push({ row: line, message: `Quantity "${quantity}" is not a number` });
      }

      create += 1;
    }
  });

  return { create, update, issues };
}

export interface CommitResult {
  jobId: string;
  created: number;
  updated: number;
  failed: number;
}

/**
 * Write the rows, and the audit trail alongside them.
 *
 * Each row's outcome lands in `import_records`, so "where did this product
 * come from" has an answer months later. A row that fails does not stop the
 * rest: a spreadsheet with one bad line should import the other 899.
 */
export async function commitImport(
  accountId: string,
  entity: ImportEntity,
  parsed: ParsedCsv,
  mapping: Record<string, string>,
  fileName: string,
  /** Where imported packages land. Stock has to be somewhere. */
  locationId?: string,
): Promise<CommitResult> {
  const { data: job, error: jobError } = await supabase
    .from('import_jobs')
    .insert({
      account_id: accountId,
      source: 'csv',
      status: 'running',
      dry_run: false,
      total_count: parsed.rows.length,
      config: { entity, fileName, mapping },
    })
    .select('id')
    .single();

  if (jobError) throw jobError;

  const records: Array<{
    entityType: string;
    externalId: string;
    entityId: string | null;
    action: 'created' | 'updated' | 'skipped' | 'failed';
    error: string | null;
  }> = [];

  let created = 0;
  let updated = 0;
  let failed = 0;

  for (const [index, row] of parsed.rows.entries()) {
    const get = (key: string) => cell(row, parsed.headers, mapping, key);

    try {
      const outcome =
        entity === 'product'
          ? await importProduct(accountId, get)
          : entity === 'customer'
            ? await importCustomer(accountId, get)
            : await importPackage(accountId, get, locationId);

      if (outcome.action === 'created') created += 1;
      if (outcome.action === 'updated') updated += 1;

      records.push({
        entityType: entity,
        externalId: get('sku') || get('email') || get('label') || String(index + 2),
        entityId: outcome.id,
        action: outcome.action,
        error: null,
      });
    } catch (error) {
      failed += 1;
      records.push({
        entityType: entity,
        externalId: get('sku') || get('email') || get('label') || String(index + 2),
        entityId: null,
        action: 'failed',
        error: (error as Error).message.slice(0, 400),
      });
    }
  }

  /*
   * The trail goes through the API: `import_records` grants SELECT and
   * nothing else, so a browser insert is refused -- silently, since
   * PostgREST reports zero rows written rather than an error. Sent in one
   * call; a hundred round trips would double the import's wall clock.
   */
  if (records.length) {
    await adminApi.imports
      .records(accountId, { jobId: job.id, records })
      .catch((error: Error) => console.error('[import] trail not written', error));
  }

  await supabase
    .from('import_jobs')
    .update({
      // `partial` is a real status: some rows landed and some did not, and a
      // job that says "succeeded" with three failures is a lie.
      status: failed === 0 ? 'succeeded' : failed === parsed.rows.length ? 'failed' : 'partial',
      processed_count: parsed.rows.length,
      finished_at: new Date().toISOString(),
      stats: { created, updated, failed },
    })
    .eq('account_id', accountId)
    .eq('id', job.id);

  return { jobId: job.id, created, updated, failed };
}

type Getter = (key: string) => string;

/** Find-or-create a taxonomy row by name, so a file can name a new brand. */
async function resolveByName(
  accountId: string,
  table: 'brands' | 'categories',
  name: string,
): Promise<string | null> {
  if (!name) return null;

  const { data: existing } = await supabase
    .from(table)
    .select('id')
    .eq('account_id', accountId)
    .ilike('name', name)
    .maybeSingle();

  if (existing) return existing.id;

  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || `imported-${Date.now()}`;

  const { data, error } = await supabase
    .from(table)
    .insert({ account_id: accountId, name, slug })
    .select('id')
    .single();

  if (error) throw error;

  return data.id;
}

async function importProduct(accountId: string, get: Getter) {
  const sku = get('sku');
  const priceCents = Math.round(Number(get('price').replace(/^\$/, '')) * 100);

  if (sku) {
    const { data: variant } = await supabase
      .from('product_variants')
      .select('id, product_id')
      .eq('account_id', accountId)
      .eq('sku', sku)
      .maybeSingle();

    if (variant) {
      const { error } = await supabase
        .from('product_variants')
        .update({ price_cents: priceCents })
        .eq('account_id', accountId)
        .eq('id', variant.id);

      if (error) throw error;

      return { action: 'updated' as const, id: variant.product_id };
    }
  }

  const [brandId, categoryId] = await Promise.all([
    resolveByName(accountId, 'brands', get('brand')),
    resolveByName(accountId, 'categories', get('category')),
  ]);

  const name = get('name');
  const slug = `${name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}-${Math.random().toString(36).slice(2, 8)}`;

  const { data: product, error } = await supabase
    .from('products')
    .insert({
      account_id: accountId,
      name,
      slug,
      status: 'draft',
      description: get('description') || null,
      brand_id: brandId,
      category_id: categoryId,
      strain_type: (get('strain_type').toLowerCase() || 'na') as never,
      thc_percent: get('thc_percent') ? Number(get('thc_percent')) : null,
      cbd_percent: get('cbd_percent') ? Number(get('cbd_percent')) : null,
      image_urls: get('image_url') ? [get('image_url')] : [],
    })
    .select('id')
    .single();

  if (error) throw error;

  const { error: variantError } = await supabase.from('product_variants').insert({
    account_id: accountId,
    product_id: product.id,
    name: get('unit') || 'Each',
    unit: (get('unit').toLowerCase() || 'each') as never,
    sku: sku || null,
    price_cents: priceCents,
    weight_grams: get('weight_grams') ? Number(get('weight_grams')) : null,
  });

  if (variantError) throw variantError;

  return { action: 'created' as const, id: product.id };
}

async function importCustomer(accountId: string, get: Getter) {
  const email = get('email');

  const { data: existing } = await supabase
    .from('customers')
    .select('id')
    .eq('account_id', accountId)
    .ilike('email', email)
    .maybeSingle();

  const row = {
    email,
    first_name: get('first_name') || null,
    last_name: get('last_name') || null,
    phone: get('phone') || null,
    birthday: get('birthday'),
    customer_type: (get('customer_type').toLowerCase() || 'recreational') as never,
    marketing_opt_in: /^(true|yes|1|y)$/i.test(get('marketing_opt_in')),
  };

  if (existing) {
    const { error } = await supabase
      .from('customers')
      .update(row)
      .eq('account_id', accountId)
      .eq('id', existing.id);

    if (error) throw error;

    return { action: 'updated' as const, id: existing.id };
  }

  const { data, error } = await supabase
    .from('customers')
    .insert({ account_id: accountId, ...row })
    .select('id')
    .single();

  if (error) throw error;

  return { action: 'created' as const, id: data.id };
}

async function importPackage(accountId: string, get: Getter, locationId?: string) {
  if (!locationId) throw new Error('Choose the location these packages arrived at.');

  const { data: variant } = await supabase
    .from('product_variants')
    .select('id')
    .eq('account_id', accountId)
    .eq('sku', get('sku'))
    .maybeSingle();

  if (!variant) throw new Error(`No variant with SKU ${get('sku')}`);

  const { data, error } = await supabase
    .from('packages')
    .insert({
      account_id: accountId,
      metrc_tag: get('label'),
      location_id: locationId,
      variant_id: variant.id,
      initial_quantity: Number(get('quantity')),
      quantity: Number(get('quantity')),
      received_at: get('received_at') || null,
    })
    .select('id')
    .single();

  if (error) throw error;

  return { action: 'created' as const, id: data.id };
}

export interface ImportJobRow {
  id: string;
  entity: string;
  fileName: string;
  status: string;
  totalCount: number;
  stats: { created?: number; updated?: number; failed?: number };
  createdAt: string;
}

export async function listImportJobs(accountId: string): Promise<ImportJobRow[]> {
  const { data, error } = await supabase
    .from('import_jobs')
    .select('id, status, total_count, stats, config, created_at')
    .eq('account_id', accountId)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) throw error;

  return (data ?? []).map((row) => {
    const config = (row.config ?? {}) as { entity?: string; fileName?: string };

    return {
      id: row.id,
      entity: config.entity ?? 'unknown',
      fileName: config.fileName ?? '',
      status: row.status,
      totalCount: row.total_count ?? 0,
      stats: (row.stats ?? {}) as ImportJobRow['stats'],
      createdAt: row.created_at,
    };
  });
}
